import type { DocumentType } from 'api';
import type { SelectOption } from 'form';
import { useTranslation } from 'i18n';
import { addError } from 'monitor';
import { useRef, useState } from 'react';
import { Button, HiddenInput, MenuItem, Select } from 'ui';
import { DescriptionOutlined } from 'ui/icons';

import { DOCUMENT_TYPE_OPTIONS } from '../../utils/document';

type Props = {
  onFileSelected: (file: File, documentType: DocumentType) => void;
};

export const AddDocumentButton = ({ onFileSelected }: Props) => {
  const { t } = useTranslation('orders');
  const [buttonClicked, setButtonClicked] = useState(false);
  const [documentType, setDocumentType] = useState<DocumentType | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!buttonClicked) {
    return (
      <Button
        onClick={() => setButtonClicked(true)}
        startIcon={<DescriptionOutlined />}
        data-testid={'add_document_button'}
      >
        {t('documents.addButton.actions.addDocument')}
      </Button>
    );
  }

  return (
    <>
      <Select
        label={t('documents.addButton.labels.documentType')}
        onChange={(event) => {
          setDocumentType(event.target.value as DocumentType);
          fileInputRef.current?.click();
        }}
      >
        {DOCUMENT_TYPE_OPTIONS.map((option: SelectOption) => (
          <MenuItem key={option.label} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <HiddenInput
        multiple={false}
        ref={fileInputRef}
        type="file"
        onChange={(event) => {
          if (!event.target.files?.length) {
            return;
          }
          if (!documentType) {
            addError(Error(t('documents.addButton.errors.documentTypeRequired')));
            return;
          }
          onFileSelected(event.target.files[0], documentType);
          setDocumentType(null);
          setButtonClicked(false);
        }}
      />
    </>
  );
};
